/**
 * Tarefas, agenda, respostas prontas e fluxos.
 *
 * Um pedaço do objeto de ações do `Provedor` (`estado.jsx`), cortado por área.
 * Recebe o contexto uma vez, dentro do `useMemo` de lá: a identidade de cada
 * ação continua estável, como era no objeto único.
 */
import * as api from "../api.js";
import { confirmar, pedirTexto } from "@/componentes/pergunta.tsx";

export function acoesDeTarefas(c) {
  const { avisar, carregarVista, casoAberto, mudar, ref, tentar } = c;
  return {
    /*
     * O quadro recarrega inteiro depois de mexer numa tarefa.
     *
     * Parece desperdício e não é: os contadores de coluna, a carga por pessoa
     * e os prazos da semana são DERIVADOS do conjunto — mover uma tarefa de
     * "a fazer" para "em andamento" muda quatro números além do cartão. Ajustar
     * na tela pediria repetir na tela as quatro contas do servidor, e é assim
     * que duas versões da mesma conta começam a discordar.
     */
    criarTarefa: (tarefa) =>
      tentar("tarefa", async () => {
        await api.criarTarefa(tarefa);
        await carregarVista("tarefas");
      }),
    moverTarefa: (id, estado) =>
      tentar("tarefa", async () => {
        await api.ajustarTarefa(id, { estado });
        await carregarVista("tarefas");
      }),

    /**
     * O arrasto do quadro, com resposta no dedo.
     *
     * Aqui a atualização é OTIMISTA, e é a única do estado que é: arrastar é um
     * gesto contínuo, e esperar a rede para o cartão pousar faz o quadro
     * parecer quebrado — a pessoa solta, nada acontece por 200ms, e ela arrasta
     * de novo. A tela reordena na hora e o servidor confirma depois.
     *
     * Falhando, o quadro VOLTA para o que o servidor tem (`carregarVista`) e o
     * recado diz o motivo: cartão que fica no lugar novo depois de uma recusa é
     * a pior das duas mentiras possíveis.
     */
    arrastarTarefa: async (id, estado, indice) => {
      const antes = ref.current.tarefas;
      /* Reordena a cópia local do jeito que o servidor vai reordenar: o cartão
         sai de onde estava e entra na posição pedida da coluna de destino. */
      const movida = antes.find((t) => t.id === id);
      if (!movida) return;
      const semEla = antes.filter((t) => t.id !== id);
      const daColuna = semEla.filter((t) => (t.estado ?? "a_fazer") === estado);
      const alvo = daColuna[Math.max(0, Math.min(indice, daColuna.length))];
      const nova = { ...movida, estado, ...(estado === "concluido" ? { concluida: 1 } : {}) };
      const reordenada = [];
      for (const t of semEla) {
        if (alvo && t.id === alvo.id) reordenada.push(nova);
        reordenada.push(t);
      }
      if (!alvo) reordenada.push(nova);
      mudar({ tarefas: reordenada });

      try {
        await api.moverTarefaNoQuadro(id, { estado, indice });
        /* Recarrega para pegar o que só o servidor sabe: os contadores de
           coluna, a carga por pessoa e o `concluida_em`. */
        await carregarVista("tarefas");
      } catch (e) {
        mudar({ tarefas: antes });
        avisar(e.message);
        await carregarVista("tarefas");
      }
    },
    ajustarTarefa: (id, mudanca) =>
      tentar("tarefa", async () => {
        await api.ajustarTarefa(id, mudanca);
        await carregarVista("tarefas");
      }),
    concluirTarefa: (id, resultado) =>
      tentar("tarefa", async () => {
        await api.concluirTarefa(id, resultado ?? null);
        await carregarVista("tarefas");
      }),
    apagarTarefa: async (id, titulo) => {
      /* Apagar tarefa não é concluir: some da trilha do escritório e ninguém
         mais sabe que aquilo foi pedido. Concluir é o caminho normal. */
      const ok = await confirmar({
        titulo: `Apagar "${titulo ?? "esta tarefa"}"?`,
        texto: "Ela sai do quadro e da carga de quem estava com ela. Para registrar que foi feita, use Concluir.",
        acao: "Apagar",
        perigo: true,
      });
      if (!ok) return;
      await tentar("tarefa", async () => {
        await api.apagarTarefa(id);
        await carregarVista("tarefas");
      });
    },

    /* A mensagem que ainda não saiu: cancelar é a única coisa que se faz com
       ela, e depois de sair não há o que cancelar. `caso` é o registro dono do
       agendamento — a rota que cancela é a de leads, onde ele nasceu. */
    cancelarAgendamentoDaAgenda: async (caso, id, previa) => {
      const ok = await confirmar({
        titulo: "Cancelar este envio?",
        texto: previa ? `Não sai: "${previa}"` : "A mensagem não será enviada na hora marcada.",
        acao: "Cancelar o envio",
        perigo: true,
      });
      if (!ok) return;
      await tentar("agenda", async () => {
        await api.cancelarAgendada(caso, id);
        await carregarVista("agenda");
      });
    },

    /* Reler a lista de prontas. Existe porque o ARQUIVO da pronta é gravado
       por rota própria (multipart), fora de `salvarRapida` — e sem reler, a
       linha da lista continua dizendo que a resposta é só texto. */
    carregarRapidas: () => carregarVista("rapidas"),

    /* Devolve a resposta gravada porque o formulário precisa do ID recém-criado:
       o arquivo escolhido ANTES de a resposta existir sobe logo depois, na rota
       de anexo (multipart), que não existe sem linha no banco. */
    salvarRapida: (id, dados) =>
      tentar("rapida", async () => {
        const r = await api.salvarRapida(id, dados);
        await carregarVista("rapidas");
        return r;
      }),
    apagarRapida: async (id, atalho) => {
      const ok = await confirmar({
        titulo: `Apagar ${atalho ?? "esta resposta"}?`,
        texto: "Quem digitar esse atalho não acha mais nada. O texto não volta.",
        acao: "Apagar",
        perigo: true,
      });
      if (!ok) return;
      await tentar("rapida", async () => {
        await api.apagarRapida(id);
        await carregarVista("rapidas");
      });
    },

    /**
     * Usar uma pronta: conta o uso e devolve o texto com as variáveis
     * resolvidas pelo caso aberto.
     *
     * Quem resolve `{nome}` é o SERVIDOR, e não a tela: o nome do cliente, o
     * número do processo e o prazo vivem no banco, e substituir aqui daria
     * duas verdades sobre o mesmo texto — uma na conversa, outra no que o
     * copiloto manda.
     */
    usarRapida: async (id) => {
      try {
        return await api.usarRapida(id, casoAberto() ?? null);
      } catch (e) {
        avisar(e.message);
        return null;
      }
    },

    ajustarFluxo: (id, mudanca) =>
      tentar("fluxo", async () => {
        const novo = await api.ajustarFluxo(id, mudanca);
        mudar((a) => ({ fluxos: a.fluxos.map((f) => (f.id === id ? novo : f)) }));
      }),

    criarFluxo: async () => {
      const nome = await pedirTexto({ titulo: "Fluxo novo", valor: "Fluxo sem nome", acao: "Criar" });
      if (!nome) return null;
      return tentar("fluxo", async () => {
        const criado = await api.criarFluxo(nome, "⚙️");
        mudar((a) => ({ fluxos: [...a.fluxos, criado] }));
        avisar(`Fluxo "${criado.nome}" criado — está desligado até você ligar.`);
        return criado;
      });
    },

    /*
     * Apagar pede confirmação porque é irreversível e o fluxo pode estar
     * conduzindo atendimento agora. O que ele já fez continua na trilha dos
     * casos — some a regra, não o histórico.
     */
    apagarFluxo: async (fluxo) => {
      if (
        !(await confirmar({
          titulo: `Apagar o fluxo "${fluxo.nome}"?`,
          texto: "O que ele já rodou continua na trilha dos casos.",
          acao: "Apagar",
          perigo: true,
        }))
      )
        return;
      await tentar("fluxo", async () => {
        await api.apagarFluxo(fluxo.id);
        mudar((a) => ({ fluxos: a.fluxos.filter((f) => f.id !== fluxo.id) }));
        avisar(`Fluxo "${fluxo.nome}" apagado.`);
      });
    },

    /*
     * O ensaio do fluxo: onde a regra para, hoje.
     *
     * Era uma navegação para o primeiro atendimento da mesa — não testava
     * nada, e "Testar" que não testa é pior que botão que não existe. Quem
     * responde é o servidor, porque é ele que sabe se o motor está de pé e o
     * que o `.env` permite.
     */
    ensaiarFluxo: (fluxo) => tentar("ensaio", () => api.ensaiarFluxo(fluxo.id)),
  };
}
